/**
 * Turning a plan into sentences.
 *
 * The server groups a plan by *change signature* -- a mechanical key like
 * `moved:marker>country` or `city:filled+title:cleared` -- so that twelve thousand rows
 * collapse into a handful of shapes an operator can actually judge. The key is stable and
 * machine-comparable, which is what makes it a good grouping key and a poor thing to read.
 * This turns it into English.
 *
 * Kept pure and separate so it can be tested without a browser, in the mould of
 * `ArtworkProviders.tsx` and its sibling test. The captions are cosmetic by design: an
 * unrecognised atom renders as itself rather than being dropped, because a caption table
 * that silently swallows a shape it has not seen is how a new kind of change becomes
 * invisible on the one screen built to show it.
 */

import type { TFunction } from "i18next";

/**
 * Field names as an operator refers to them, where that differs from the column.
 *
 * Looked up rather than held in a module-level table: a constant built at import time would
 * freeze the copy in whatever language happened to be active then. A field with no entry
 * renders as its own column name, which is the old table's fallback.
 */
function label(field: string, t: TFunction): string {
  return t(`reparse.field.${field}`, { defaultValue: field });
}

const TRANSITIONS: Record<string, (field: string, t: TFunction) => string> = {
  filled: (field, t) => t("reparse.atomFilled", { field }),
  cleared: (field, t) => t("reparse.atomCleared", { field }),
  changed: (field, t) => t("reparse.atomChanged", { field }),
};

/** One atom of a signature, in words. */
export function captionAtom(atom: string, t: TFunction): string {
  if (atom.startsWith("moved:")) {
    const [from, to] = atom.slice("moved:".length).split(">");
    if (from && to) return t("reparse.atomMoved", { from: label(from, t), to: label(to, t) });
    return atom;
  }
  const [field, transition] = atom.split(":");
  const render = transition ? TRANSITIONS[transition] : undefined;
  return render && field ? render(label(field, t), t) : atom;
}

/**
 * A whole signature, in words.
 *
 * The empty signature means a plan item that changes nothing, which should not exist -- the
 * planner does not write one. It is captioned rather than hidden so that if one ever does
 * appear, it appears.
 */
export function captionSignature(signature: string, t: TFunction): string {
  if (!signature) return t("reparse.noChange");
  // Deliberately not sentence-cased. Russian capitalisation is not English's, so each atom's
  // catalogue value carries whatever case it should start with.
  return signature
    .split("+")
    .map((atom) => captionAtom(atom, t))
    .join(", ");
}

export interface CountLike {
  planned_count: number;
  applied_count: number;
  skipped_count: number;
  state: string;
}

/**
 * The one-line summary of what a run did or would do.
 *
 * Deferred and refused rows are named rather than folded into a total. "We chose not to move
 * these" and "there was nothing to do" are different facts about the archive, and a single
 * number that covers both is how the difference gets lost.
 */
export function summarise(run: CountLike, t: TFunction): string {
  switch (run.state) {
    case "planning":
      return t("reparse.summaryPlanning", { planned: run.planned_count });
    case "planned":
      if (run.planned_count === 0) return t("reparse.summaryNothingToChange");
      return t("reparse.summaryPlanned", { count: run.planned_count });
    case "applying":
      return t("reparse.summaryApplying", {
        applied: run.applied_count,
        planned: run.planned_count,
      });
    case "succeeded":
      // One key per whole sentence rather than two joined fragments: a translation has to be
      // free to put the leftovers first, or to punctuate the join its own way.
      return run.skipped_count
        ? t("reparse.summarySucceededWithSkipped", {
            count: run.applied_count,
            skipped: run.skipped_count,
          })
        : t("reparse.summarySucceeded", { count: run.applied_count });
    case "failed":
      return t("reparse.summaryFailed");
    case "cancelled":
      return run.applied_count
        ? t("reparse.summaryCancelledAfter", { count: run.applied_count })
        : t("reparse.summaryCancelled");
    default:
      return run.state;
  }
}

/**
 * How long until a hold lapses, as a short phrase.
 *
 * Always forward-looking and never negative: an expired hold is not shown as "-3m", it is
 * simply over. The panel drops it on the next poll, and until then "expired" is the honest
 * word for a switch that is no longer doing anything.
 */
export function untilText(untilIso: string, nowMs: number, t: TFunction): string {
  const remaining = Date.parse(untilIso) - nowMs;
  if (!Number.isFinite(remaining) || remaining <= 0) return t("reparse.expired");
  const minutes = Math.round(remaining / 60_000);
  if (minutes < 60) return t("reparse.minutesLeft", { minutes });
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest
    ? t("reparse.hoursMinutesLeft", { hours, minutes: rest })
    : t("reparse.hoursLeft", { hours });
}

/**
 * Whether a plan can still be approved.
 *
 * Three independent reasons it cannot, and the caller renders whichever applies. A stale plan
 * is the interesting one: the parser moved after the plan was built, so the plan no longer
 * predicts what would be written, and approving it would mean approving one thing and getting
 * another.
 */
export function applyBlockedReason(
  run: {
    state: string;
    stale: boolean;
    planned_count: number;
  },
  t: TFunction,
): string | null {
  if (run.state !== "planned") return null;
  if (run.stale) return t("reparse.blockedStale");
  if (run.planned_count === 0) return t("reparse.blockedEmpty");
  return null;
}

/**
 * Scale week counts to a bar height, 0..1.
 *
 * Relative to the busiest week rather than to an absolute number, because what the strip is
 * for is spotting *where* a repair concentrates — the weeks the station changed how it
 * writes its titles. An absolute scale would flatten a whole archive whose worst week is
 * forty rows, which is exactly the archive where the shape matters most.
 *
 * A week with any rows at all gets a floor, so "a few" never renders as "none".
 */
export function weekHeights(
  weeks: readonly { week: string; count: number }[],
): { week: string; count: number; height: number }[] {
  const peak = weeks.reduce((most, entry) => Math.max(most, entry.count), 0);
  if (peak === 0) return weeks.map((entry) => ({ ...entry, height: 0 }));
  return weeks.map((entry) => ({
    ...entry,
    height: entry.count === 0 ? 0 : Math.max(0.08, entry.count / peak),
  }));
}

/**
 * The window covering the week that starts at `weekIso`, as the API wants it.
 *
 * Half-open, matching how the planner filters: a run scoped to a week must not also catch
 * the show that starts the moment the next week begins.
 */
export function weekWindow(weekIso: string): { start: string; end: string } {
  const start = new Date(weekIso);
  const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
  return { start: start.toISOString(), end: end.toISOString() };
}

/**
 * Rows the archive knows are on an older parser than the one running now.
 *
 * Rows never touched by a repair (`fingerprint: null`) are counted separately and are not
 * "behind" — nobody has ever claimed anything about them. Conflating the two would turn a
 * fresh deployment into an alarming backlog on day one.
 */
export function behindCount(
  coverage: readonly { fingerprint: string | null; count: number }[],
  current: string,
): number {
  return coverage
    .filter((entry) => entry.fingerprint !== null && entry.fingerprint !== current)
    .reduce((total, entry) => total + entry.count, 0);
}
